import { DateTime } from "luxon";
import { ReactNode, useState } from "react";
import { Pressable } from "react-native";

import { BottomSheet } from "@/components/ui/bottom-sheet";

interface Props {
  children?: ReactNode;
  description: string;
  endISO: null | string;
  geo: { lat: number; lng: number };
  location: string;
  startISO: null | string;
  title: string;
  venue: string;
}

export default function AddToCalendar({
  children,
  description,
  endISO,
  geo,
  location,
  startISO,
  title,
  venue,
}: Props) {
  const [open, setOpen] = useState(false);

  function handlePress() {
    if (!startISO || !endISO) return;
    if (isAppleDevice()) {
      setOpen(true);
    } else {
      openGoogle();
    }
  }

  function openGoogle() {
    if (!startISO || !endISO) return;
    const start = toCalendarDate(startISO);
    const end = toCalendarDate(endISO);
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`;
    window.open(url, "_blank");
    setOpen(false);
  }

  function downloadIcal() {
    if (!startISO || !endISO) return;
    const start = toCalendarDate(startISO);
    const end = toCalendarDate(endISO);
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      `GEO:${geo.lat};${geo.lng}`,
      `X-APPLE-STRUCTURED-LOCATION;VALUE=URI;X-ADDRESS=${location};X-APPLE-RADIUS=100;X-TITLE=${venue}:geo:${geo.lat},${geo.lng}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "event.ics";
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  }

  return (
    <>
      <Pressable onPress={handlePress}>{children}</Pressable>

      <BottomSheet
        onClose={() => setOpen(false)}
        options={[
          { label: "add to Google Calendar", onPress: openGoogle },
          { label: "add to iCal", onPress: downloadIcal },
        ]}
        visible={open}
      />
    </>
  );
}

function isAppleDevice() {
  const ua = navigator.userAgent;
  return /Macintosh|iPhone|iPad|iPod/.test(ua);
}

function toCalendarDate(iso: string) {
  return DateTime.fromISO(iso).toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
}
